<script lang="ts">
  import Layout from "$app/layouts/Pdf.svelte"
  import { onMount } from "svelte"
  import ApexCharts from "apexcharts"
  import { ChevronDown, ChevronRight, Sparkles } from "lucide-svelte"

  import InitialNote from "./Options/InitialNote.svelte"
  import ScoreDetails from "./Options/ScoreDetails.svelte"
  import CategoryStatus from "./Options/CategoryStatus.svelte"
  import TrustDetails from "./Options/TrustDetails.svelte"
  import AlignmentDetails from "./Options/AlignmentDetails.svelte"
  import DecisionDetails from "./Options/DecisionDetails.svelte"
  import Alignment from "./Options/Alignment.svelte"
  import DecisionMaking from "./Options/DecisionMaking.svelte"
  import Trust from "./Options/Trust.svelte"
  import WhatToDoNext from "./WhatToDoNext.svelte"
  import FastAttackTable from "./FastAttackTable.svelte"

  let props = $props()
  let { questions } = props
  let response = $state(props.response)

  //console.log(questions)
  //console.log(response)

  import alignment from "@images/report/alignment.png"
  import decision from "@images/report/decision.png"
  import trust from "@images/report/trust-icon.png"

  import map from "@images/map.jpg"
  import fast_attack from "@images/fast-attack-logo.png"
  import marc_submarine from "@images/report/marc_submarine.jpg"

  let score: HTMLDivElement = $state(null)
  let snapshot: HTMLDivElement = $state(null)

  let showTrustDetails = $state(false)
  let showAlignmentDetails = $state(false)
  let showDecisionDetails = $state(false)

  let scoreHeading = $derived(() => {
    if (response.score.total < 25) {
      return "CRITICAL"
    } else if (response.score.total < 50) {
      return "LOW"
    } else if (response.score.total < 80) {
      return "EMERGING"
    } else if (response.score.total < 100) {
      return "HIGH"
    } else if (response.score.total == 100) {
      return "ELITE"
    }
  })

  let scoreColor = $derived(() => {
    if (response.score.total < 25) {
      return "#ef4444"
    } else if (response.score.total < 50) {
      return "#fdba74"
    } else if (response.score.total < 80) {
      return "#eab308"
    } else if (response.score.total < 100) {
      return "#10b981"
    } else if (response.score.total == 100) {
      return "#16a34a"
    }
  })

  onMount(() => {
    var score_options = {
      series: [response.score.total],
      colors: [scoreColor()],
      chart: {
        height: 350,
        type: "radialBar",
        offsetY: -10,
        animations: {
          enabled: false
        },
        zoom: {
          enabled: false
        }
      },
      plotOptions: {
        radialBar: {
          startAngle: -135,
          endAngle: 135,
          dataLabels: {
            name: {
              fontSize: "16px",
              color: scoreColor(),
              offsetY: 120
            },
            value: {
              offsetY: 76,
              fontSize: "22px",
              color: scoreColor(),
              formatter: function (val) {
                return val + "%"
              }
            }
          }
        }
      },
      fill: {
        type: "gradient",
        gradient: {
          shade: "dark",
          type: "horizontal",
          shadeIntensity: 0.75,
          gradientToColors: [scoreColor()],
          inverseColors: true,
          opacityFrom: 1,
          opacityTo: 1,
          stops: [0, 100]
        }
      },
      stroke: {
        dashArray: 4
      },
      labels: [scoreHeading()]
    }

    var score_chart = new ApexCharts(score, score_options)
    score_chart.render()

    var options2 = {
      series: [
        response.score.trust,
        response.score.decision,
        response.score.alignment,
        response.score.total
      ],
      chart: {
        height: 390,
        type: "radialBar",
        animations: {
          enabled: false
        },
        zoom: {
          enabled: false
        }
      },
      plotOptions: {
        radialBar: {
          offsetY: 0,
          startAngle: 0,
          endAngle: 270,
          hollow: {
            margin: 5,
            size: "30%",
            background: "transparent",
            image: undefined
          },
          dataLabels: {
            name: {
              show: false
            },
            value: {
              show: false
            }
          },
          barLabels: {
            enabled: true,
            useSeriesColors: true,
            offsetX: -8,
            fontSize: "16px",
            formatter: function (seriesName, opts) {
              return (
                seriesName + ":  " + opts.w.globals.series[opts.seriesIndex]
              )
            }
          }
        }
      },
      colors: ["#1ab7ea", "#0084ff", "#39539E", "#0077B5"],
      labels: ["Trust", "Decision Agility", "Alignment", "Total Score"],
      responsive: [
        {
          breakpoint: 480,
          options: {
            legend: {
              show: false
            }
          }
        }
      ]
    }

    var chart2 = new ApexCharts(snapshot, options2)
    chart2.render()
  })
</script>

<Layout>
  <div class="mx-auto page-break">
    <div
      class="w-full bg-blue-950 py-6 mx-auto flex items-center justify-between gap-x-4 px-6"
    >
      <div>
        <h2
          class="text-xl font-semibold tracking-tight text-gray-100 sm:text-4xl flex items-center"
        >
          Fast Attack Readiness Report
        </h2>
      </div>
      <img src={fast_attack} class="w-40 h-auto" alt="Fast Attack Logo" />
    </div>
    <div class="px-4 lg:px-0 py-8 lg:py-12 mx-auto bg-white">
      <div class="px-2 lg:px-8">
        <div class="mx-auto">
          <p
            class="text-xl font-medium text-pretty text-blue-950 text-left sm:text-2xl"
          >
            How Ready Is Your Leadership Team to Navigate Disruption?
          </p>
          <p
            class="mt-6 text-lg italic text-left font-medium text-pretty text-gray-700 sm:text-xl/8"
          >
            Based on your results from your Readiness Score Quiz
          </p>

          <div class="max-w-sm mx-auto h-1 bg-blue-900 rounded mt-8"></div>
        </div>
      </div>

      <div
        class="border-l-4 border-gray-400 bg-gray-50 px-4 py-3 sm:py-6 mx-auto mt-12"
      >
        <InitialNote score={response.score} />
      </div>

      <div class="mt-12">
        <p
          class="text-lg italic font-medium text-pretty text-blue-950 text-left"
        >
          Read on to discover your Readiness Score, what it means for you and
          your team and what you can do to improve.
        </p>
      </div>
    </div>
  </div>

  <div class="mx-auto page-break text-sm">
    <div class="grid grid-cols-2 gap-x-4">
      <div class="bg-blue-900 p-6 rounded-sm">
        <h2
          class="text-2xl font-semibold tracking-tight text-gray-100 text-left"
        >
          Your readiness score is...
        </h2>

        <div class="flex justify-start items-center w-full mt-4">
          <div class="w-full" bind:this={score}></div>
        </div>

        <div class="max-w-xs mx-auto h-1 bg-blue-50 rounded"></div>
        <div class="mt-6 max-w-56 mx-auto grid grid-cols-2 gap-1">
          <div>
            <p class="font-medium text-base text-gray-200">Below 25%</p>
          </div>
          <div>
            <p class="font-bold text-base text-red-400">CRITICAL</p>
          </div>
          <div>
            <p class="font-medium text-base text-gray-200">25 - 50%</p>
          </div>
          <div>
            <p class="font-bold text-base text-orange-200">LOW</p>
          </div>

          <div>
            <p class="font-medium text-base text-gray-200">50 - 80%</p>
          </div>
          <div>
            <p class="font-bold text-base text-yellow-500">EMERGING</p>
          </div>

          <div>
            <p class="font-medium text-base text-gray-200">80 - 99%</p>
          </div>
          <div>
            <p class="font-bold text-base text-emerald-300">HIGH</p>
          </div>

          <div>
            <p class="font-bold text-base text-gray-100">100%</p>
          </div>
          <div>
            <p class="font-bold text-base text-green-500">ELITE</p>
          </div>
        </div>
      </div>

      <div class="py-6 px-2 lg:px-8 text-sm">
        <ScoreDetails score={response.score} />
      </div>
    </div>

    <div
      class="mx-auto bg-gray-50 rounded shadow-sm mt-8 border-l-4 border-blue-800"
    >
      <div class="px-8 py-4">
        <p
          class="mt-2 text-xl font-semibold tracking-tight text-gray-900 lg:text-3xl"
        >
          What Your Score Means
        </p>
        <p class="mt-4 sm:mt-6 text-base lg:text-lg/8 text-gray-700">
          Keep reading for a detailed breakdown of your score, including key
          actions steps you can take to improve your team's readiness.
        </p>
        <dl class="mt-5 sm:mt-8 text-base/7 text-gray-600">
          <div class="relative pl-9">
            <dt class="inline font-semibold text-gray-900">
              <Sparkles class="absolute top-1 left-1 size-5 text-blue-600 " />
              Free 30-Minute Debrief
            </dt>
            <dd class="inline">
              Additionally, Marc Koehler is available for a 30-minute Readiness
              Debrief where he can unpack your score and create a personalized
              action plan for your team.
              <span class="font-bold italic text-sm"
                >You will also recieve an exclusive coupon code for a discount
                on the alignment test.</span
              >
            </dd>
          </div>
        </dl>
        <a
          href="https://calendly.com/marckoehler/readiness-check"
          target="_blank"
          class="mt-6 inline-flex items-center rounded-md bg-blue-600 px-6 py-2.5 text-base font-semibold text-white shadow-xs hover:bg-blue-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500"
          >Schedule My Readiness Debrief</a
        >
      </div>
    </div>
  </div>
  <div class="mx-auto page-break">
    <h2
      class="mt-4 text-xl font-semibold tracking-tight text-pretty text-gray-950 sm:text-4xl"
    >
      Readiness Snapshot
    </h2>

    <p class="mt-12 text-lg text-gray-700">
      Your Readiness Score is determined from how you scored in the key areas of <span
        class="font-bold">Trust</span
      >,
      <span class="font-bold">Decision Agility</span>
      and <span class="font-bold">Alignment</span>. Each of the 15 questions
      from the quiz are mapped to one of these areas, and your results reflect
      your strength in each.
    </p>

    <p class="mt-6 text-lg text-gray-700">
      Here is an overview of how you scored in each area, from which we
      calculate your total readiness score.
    </p>

    <div class="w-full mx-2 mt-8" bind:this={snapshot}></div>

    <div class="mt-12">
      <p class="text-lg italic font-medium text-pretty text-blue-950 text-left">
        Now we will break down each of these key areas of your score in detail,
        with insights on what you’re doing well and where you can improve.
      </p>
    </div>
  </div>

  <div class="mx-auto page-break">
    <div class="bg-white border border-gray-200 rounded-lg">
      <div
        class="relative grid grid-cols-2 gap-x-3 w-full text-left bg-gray-50"
      >
        <div class="flex gap-x-2 md:gap-x-3 items-center px-2 py-5 sm:px-6">
          <img
            src={trust}
            alt=""
            class="size-8 md:size-16 flex-none rounded-full bg-gray-50"
          />
          <div class="min-w-0 flex-auto">
            <p class="text-lg/6 md:text-2xl/6 font-semibold text-gray-900">
              Trust
            </p>
          </div>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 py-3 gap-y-4">
          <div
            class="flex justify-end md:justify-start items-center gap-x-3 px-3"
          >
            <div class="flex flex-col">
              <p
                class="text-xs md:text-sm/6 text-gray-900 text-right md:text-left"
              >
                STATUS
              </p>
              <CategoryStatus score={response.score.trust} />
            </div>
          </div>
          <div class="flex justify-end items-center gap-x-4 px-3">
            <div class="sm:flex sm:flex-col sm:items-end">
              <p class="text-xs md:text-sm/6 text-gray-900 text-right">
                YOUR SCORE
              </p>
              <p
                class="mt-1 text-base md:text-2xl/5 font-bold text-gray-800 text-right"
              >
                {response.score.trust}%
              </p>
            </div>
          </div>
        </div>
      </div>
      <div class="col-span-2 text-left border-t border-gray-200">
        <TrustDetails />
      </div>
    </div>
  </div>
  <div class="mx-auto page-break">
    <div class="bg-white border border-gray-200 rounded-lg">
      <div
        class="relative grid grid-cols-2 gap-x-3 w-full text-left bg-gray-50"
      >
        <div class="flex gap-x-2 md:gap-x-3 items-center px-2 py-5 sm:px-6">
          <img
            src={alignment}
            alt=""
            class="size-8 md:size-16 flex-none rounded-full bg-gray-50"
          />
          <div class="min-w-0 flex-auto">
            <p class="text-lg/6 md:text-2xl/6 font-semibold text-gray-900">
              Alignment
            </p>
          </div>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 py-3 gap-y-4">
          <div
            class="flex justify-end md:justify-start items-center gap-x-3 px-3"
          >
            <div class="flex flex-col">
              <p
                class="text-xs md:text-sm/6 text-gray-900 text-right md:text-left"
              >
                STATUS
              </p>
              <CategoryStatus score={response.score.alignment} />
            </div>
          </div>
          <div class="flex justify-end items-center gap-x-4 px-4">
            <div class="sm:flex sm:flex-col sm:items-end">
              <p class="text-xs md:text-sm/6 text-gray-900 text-right">
                YOUR SCORE
              </p>
              <p
                class="mt-1 text-base md:text-2xl/5 font-bold text-gray-800 text-right"
              >
                {response.score.alignment}%
              </p>
            </div>
          </div>
        </div>
        <div class="col-span-2 text-left bg-white border-t border-gray-200">
          <AlignmentDetails />
        </div>
      </div>
    </div>
  </div>
  <div class="mx-auto page-break">
    <div class="bg-white border border-gray-200 rounded-lg">
      <div
        class="relative grid grid-cols-2 gap-x-3 hover:bg-gray-50 w-full text-left bg-gray-50"
      >
        <div
          class="flex gap-x-2 md:gap-x-3 items-center px-2 py-5 sm:px-6 bg-gray-50"
        >
          <img
            src={decision}
            alt=""
            class="size-8 md:size-16 flex-none rounded-full bg-gray-50"
          />
          <div class="min-w-0 flex-auto">
            <p class="text-lg/6 md:text-2xl/6 font-semibold text-gray-900">
              Decision-Making
            </p>
          </div>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 py-3 gap-y-4">
          <div
            class="flex justify-end md:justify-start items-center gap-x-3 px-3"
          >
            <div class="flex flex-col">
              <p
                class="text-xs md:text-sm/6 text-gray-900 text-right md:text-left"
              >
                STATUS
              </p>
              <CategoryStatus score={response.score.decision} />
            </div>
          </div>
          <div class="flex justify-end items-center gap-x-4 px-4">
            <div class="sm:flex sm:flex-col sm:items-end">
              <p class="text-xs md:text-sm/6 text-gray-900 text-right">
                YOUR SCORE
              </p>
              <p
                class="mt-1 text-base md:text-2xl/5 font-bold text-gray-800 text-right"
              >
                {response.score.decision}%
              </p>
            </div>
          </div>
        </div>
        <div class="col-span-2 text-left bg-white border-t border-gray-200">
          <DecisionDetails />
        </div>
      </div>
    </div>
  </div>
  <div class="mx-auto page-break">
    <h2
      class="text-2xl font-semibold tracking-tight text-pretty text-gray-950 sm:text-4xl text-center"
    >
      {#if response.score.total < 25}
        Where You’re Breaking Down
      {:else if response.score.total < 50}
        Where You’re Losing Traction
      {:else if response.score.total < 80}
        Where You Can Improve
      {:else if response.score.total < 100}
        Where You’re Still Vulnerable
      {:else}
        Where You Still Have Risk
      {/if}
    </h2>

    <Alignment {questions} {response} pdf_mode={true} />
  </div>
  <div class="mx-auto page-break">
    <DecisionMaking {questions} {response} pdf_mode={true} />
  </div>
  <div class="mx-auto page-break">
    <Trust {questions} {response} pdf_mode={true} />
  </div>

  <div class="mx-auto page-break">
    <div class="mx-auto px-2">
      <h1
        class="mt-2 text-3xl font-semibold tracking-tight text-pretty text-gray-900"
      >
        What Fast Attack Leadership Looks Like
      </h1>

      <FastAttackTable />

      <section class="mt-5 lg:mt-8 grid grid-cols-1">
        <div>
          <p class="text-base text-gray-700">
            From 400 feet under the ocean to boardrooms facing market
            volatility, Marc Koehler built the Fast Attack Leadership Framework
            to deliver clarity, autonomy, and speed - in high-stakes
            environments.
          </p>
          <h2
            class="mt-4 text-lg font-semibold tracking-tight text-pretty text-gray-900"
          >
            Here's what ready teams do differently:
          </h2>
          <div class="grid grid-cols-2 gap-4">
            <div>
              <ul class="list-disc ml-8">
                <li class="mt-1 lg:mt-4 text-sm/7 text-gray-600">
                  Act with precision - even under pressure
                </li>
                <li class="mt-1 lg:mt-4 text-sm/7 text-gray-600">
                  Make distributed decisions at speed
                </li>
                <li class="mt-1 lg:mt-4 text-sm/7 text-gray-600">
                  Know the mission, live the values, and own their lane
                </li>
              </ul>
            </div>
            <div>
              <ul class="list-disc ml-8">
                <li class="mt-1 lg:mt-4 text-sm/7 text-gray-600">
                  Rally fast when the environment shifts
                </li>
                <li class="mt-1 lg:mt-4 text-sm/7 text-gray-600">
                  Hold each other accountable with trust and transparency
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <h2
        class="mt-4 text-lg font-semibold tracking-tight text-pretty text-gray-900"
      >
        Here are the steps you can do next to get there:
      </h2>

      <WhatToDoNext />
    </div>
  </div>

  <div class="mx-auto page-break">
    <h1 class="text-3xl font-semibold tracking-tight text-pretty text-gray-900">
      What to Do Next
    </h1>

    <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
      <div class="bg-blue-50 rounded shadow">
        <p
          class="text-base/8 font-bold text-gray-100 bg-blue-800 py-2 px-4 rounded-t"
        >
          1. Take the Fast Attack Alignment Test
        </p>
        <div class="py-2 px-4 lg:px-8">
          <p class="text-sm/6 text-gray-600">
            This team-based diagnostic is the next step after your personal
            Readiness Quiz. It measures how aligned (or misaligned) your
            leadership team really is - across strategy, communication, and
            decision-making.
          </p>

          <div class="mt-6 flex justify-center">
            <a
              href="https://app.leadwithpurpose.com/alignment"
              target="_blank"
              class="rounded-md bg-blue-700 px-4 py-2 text-sm font-semibold text-white shadow-xs hover:bg-blue-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
              >Take the Test</a
            >
          </div>

          <p class="mt-6">
            <span
              ><strong class="text-sm font-semibold text-gray-900"
                >Why it matters:</strong
              > If you’re carrying your team on your back, or if alignment seems
              fine until it’s not, the Fast Attack Alignment Test will reveal the
              real story.</span
            >
          </p>
          <p class="mt-4 italic text-sm">
            This test is used by elite leadership teams navigating high-stakes
            growth, transitions, and turnarounds.
          </p>
        </div>
      </div>

      <div class="border border-teal-200 rounded shadow">
        <h2
          class="text-base font-bold tracking-tight text-black bg-teal-50 py-2 px-4 rounded-t"
        >
          2. Or...Talk to Marc Directly
        </h2>
        <div class="py-4 px-4 lg:px-8">
          <p class="text-sm/6">
            Marc Koehler has trained thousands of leaders - from submarine crews
            to Fortune 500 teams - in how to build agile, aligned organizations
            that thrive under pressure.
          </p>

          <p class="text-sm/6 mt-4 sm:mt-6">
            Take advantage of our free 30-minute Readiness Debrief where Marc
            will unpack your score, explore your challenges, and create a next
            step tailored to your team’s stage.
            <span class="font-bold italic text-sm"
              >You will also receive an exclusive coupon code for a discount on
              the alignment test.</span
            >
          </p>
          <div class="mt-6 flex justify-center mb-2">
            <a
              href="https://calendly.com/marckoehler/readiness-check"
              target="_blank"
              class="rounded-md bg-blue-700 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-blue-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 text-center"
              >Schedule My Free Readiness Debrief</a
            >
          </div>
        </div>
      </div>
    </div>

    <div class="mt-8">
      <h1 class="text-3xl font-semibold tracking-tight text-gray-900">
        Final Word
      </h1>

      <p class="mt-6 text-xl text-gray-700">
        The teams that win in today’s world aren’t the ones with the most
        strategy decks. They’re the ones ready to execute.
      </p>

      <p
        class="mt-4 text-base font-semibold text-gray-800 bg-sky-100 px-4 py-2 italic rounded"
      >
        You don’t need another leadership offsite. You need a new system.
      </p>
      <p class="mt-6 text-2xl text-blue-800">Let’s build it together.</p>
    </div>
  </div>
</Layout>

<style>
  * {
    color-adjust: exact !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  @page {
    margin: 0;
    width: 8.5in;
    height: 11in;
  }
  p {
    margin-bottom: 0.5rem;
  }
  /*
  * width: 8.25in
  * height: 11.75in
  */

  .page-break {
    break-after: page;
  }

  .print-size {
    width: 8.6in;
    height: 12.15in;
  }
</style>
